import React, { useState, useContext } from 'react';
import { CourseDesignContext } from '../context/CourseDesignContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookOpen, faVideo, faFileAlt, faLink } from '@fortawesome/free-solid-svg-icons';
import './SyllabusLinkAdder.css';

const SyllabusLinkAdder = () => {
  const { courseDesignState, handleChange, removeFromDatabase } = useContext(CourseDesignContext);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [linkEditors, setLinkEditors] = useState([]); // Array for new links being added

  // Get all items sorted by dataIndex
  const allItems = courseDesignState
    .filter(item => item.dataType === 'topic' || item.dataType === 'sectionName')
    .sort((a, b) => parseInt(a.dataIndex, 10) - parseInt(b.dataIndex, 10));

  // Filter topics that have syllabus links enabled (questionLogic === 'yes')
  const topicsWithSyllabusLinks = allItems.filter(item => 
    item.dataType === 'topic' && item.questionLogic === 'yes'
  );

  // Get syllabus link items for a specific topic
  const getSyllabusLinksForTopic = (topicID) => {
    return courseDesignState
      .filter(item => item.dataType === 'syllabusLinkItem' && item.questionType === topicID)
      .sort((a, b) => parseInt(a.dataIndex, 10) - parseInt(b.dataIndex, 10));
  };

  // Get icon component based on link type
  const getIconForLinkType = (linkType) => {
    switch (linkType) {
      case 'reading': return faBookOpen;
      case 'video': return faVideo;
      case 'lecturenotes': return faFileAlt;
      case 'html': return faLink;
      default: return faLink;
    }
  };

  const handleEditClick = (topic) => {
    setSelectedTopic(topic);

    // Populate linkEditors with existing syllabus links in the correct format
    const newLinkEditors = getSyllabusLinksForTopic(topic.dataID)
      .map(link => ({
        id: link.dataID,
        url: link.questionLogic,
        iconType: link.dataText, // Assuming dataText holds the icon type
      }));

    setLinkEditors(newLinkEditors); // Set the linkEditors state
  };

// SyllabusLinkAdder.jsx
const handleAddLink = () => {
  const newId = Date.now();  // or a UUID, but be consistent
  const newLinkEditor = { id: newId, url: '', iconType: 'reading' };

  setLinkEditors([...linkEditors, newLinkEditor]);

  // Also create the DB entry:
  handleChange(
    'syllabusLinkItem',
    newId.toString(),
    'reading',              // iconType stored as dataText
    {
      questionLogic: '',    // initially empty URL
      questionType: selectedTopic?.dataID,
      // dataIndex: ??? if desired
    }
  );
};


// SyllabusLinkAdder.jsx
const handleUpdateLink = (id, updates) => {
  // First update local state
  const updatedEditors = linkEditors.map(editor =>
    editor.id === id ? { ...editor, ...updates } : editor
  );
  setLinkEditors(updatedEditors);

  // Grab the merged editor fields after local state update
  const updatedEditor = updatedEditors.find(e => e.id === id);

  // Now call handleChange with the correct signature
  handleChange(
    'syllabusLinkItem',
    id.toString(),            // dataID must be a string
    updatedEditor.iconType,   // This becomes the "value" -> stored in dataText
    {
      questionLogic: updatedEditor.url,            // stored in questionLogic
      questionType: selectedTopic?.dataID,         // keeps it tied to the topic
      // dataIndex: ??? if you want/need to store it
    }
  );
};


  return (
    <div className="syllabus-link-adder">
      <div className="left-column">
        <div className="days-list">
          {allItems.map(item => {
            if (item.dataType === 'sectionName') {
              return (
                <div key={item.dataID} className="section-name-display">
                  {item.dataText}
                </div>
              );
            } else if (item.dataType === 'topic' && item.questionLogic === 'yes') {
              const links = getSyllabusLinksForTopic(item.dataID);
              return (
                <div key={item.dataID} className="topic-day-row">
                  <div className="topic-name">{item.dataText || 'Untitled Topic'}</div>
                  <div className="topic-links">
                    {links.map(link => (
                      <FontAwesomeIcon
                        key={link.dataID}
                        icon={getIconForLinkType(link.dataText)}
                        className="topic-link-icon"
                        title={link.questionLogic}
                        onClick={() => window.open(link.questionLogic, '_blank', 'noopener,noreferrer')}
                      />
                    ))}
                    <button 
                      className="edit-day-button"
                      onClick={() => handleEditClick(item)}
                    >
                      Edit
                    </button>
                  </div>
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>

      <div className="right-column">
        {selectedTopic ? (
          <>
            <h3>{selectedTopic.dataText}</h3>

            {linkEditors.map(editor => (
              <div key={editor.id} className="link-editor">
                <div className="icon-row">
                  <FontAwesomeIcon
                    icon={faBookOpen}
                    className={`link-icon ${editor.iconType === 'reading' ? 'selected-icon' : ''}`}
                    onClick={() => handleUpdateLink(editor.id, { iconType: 'reading' })}
                  />
                  <FontAwesomeIcon
                    icon={faVideo}
                    className={`link-icon ${editor.iconType === 'video' ? 'selected-icon' : ''}`}
                    onClick={() => handleUpdateLink(editor.id, { iconType: 'video' })}
                  />
                  <FontAwesomeIcon
                    icon={faFileAlt}
                    className={`link-icon ${editor.iconType === 'lecturenotes' ? 'selected-icon' : ''}`}
                    onClick={() => handleUpdateLink(editor.id, { iconType: 'lecturenotes' })}
                  />
                  <FontAwesomeIcon
                    icon={faLink}
                    className={`link-icon ${editor.iconType === 'html' ? 'selected-icon' : ''}`}
                    onClick={() => handleUpdateLink(editor.id, { iconType: 'html' })}
                  />
                </div>
                <input
                  type="text"
                  placeholder="URL"
                  className="url-input"
                  value={editor.url}
                  onChange={(e) => handleUpdateLink(editor.id, { url: e.target.value })}
                />
               <button
  className="delete-link-button"
  onClick={() => {
    removeFromDatabase('syllabusLinkItem', editor.id); // Remove from database
    setLinkEditors(linkEditors.filter(item => item.id !== editor.id)); // Remove from state
  }}
>
  Delete
</button>

                <button
                  className="view-link-button"
                  onClick={() => window.open(editor.url, '_blank', 'noopener,noreferrer')}
                >
                  View
                </button>
              </div>
            ))}

            <button className="add-link-button" onClick={handleAddLink}>
              Add New Syllabus Link
            </button>
          </>
        ) : (
          <div className="no-selection">
            <p>Select a day to edit its syllabus links.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SyllabusLinkAdder;
